import { DrawAction, DrawActionType } from '@engoo/realtime-protocol/draw';
import { Trace } from '@engoo/realtime-protocol/draw/trace';
import { Set, is } from 'immutable';
import { drawReducer, DrawState } from 'app/models/rtc/draw/reducer';
import { isTraceAction, TraceAction, isStartTraceAction } from 'app/models/rtc/draw/trace';
import { EntityStringId } from 'app/types/entity';
import {
  IPersistedAction,
  makeStreamPosition,
} from '@engoo/realtime-protocol/persisted';
import { makeTimestamp } from '@engoo/realtime-protocol/util/timeSchemas';
import { ICompressionResult } from 'app/services/rtc/sync/documentmanagers/lazy';
import { mapIterable } from 'app/utils/iterable';

const DEDUP_TYPES = Set<DrawActionType>([
  DrawActionType.Switch,
]);

/**
 * Turns redundant state change actions into `ICompressionResult`
 * without actions
 *
 * Needs `user` id and `DrawState` in order to speculatively check
 * if the action would change user state.
 *
 * No-op if `user` is not provided.
 */
function* clearRedundantStateChanges(
  actions: Iterable<ICompressionResult<DrawAction>>,
  state: DrawState = new DrawState(),
  user?: EntityStringId,
): Iterable<ICompressionResult<DrawAction>> {
  let cache = state;

  if (!user) {
    yield* actions;
  }

  for (const { action, compressed } of actions) {
    if (action && DEDUP_TYPES.has(action.type)) {
      // Pretend to be a persisted action and speculatively apply
      // the action on state
      const persistedAction: DrawAction & IPersistedAction = {
        ...action,
        position: makeStreamPosition(Number.MAX_SAFE_INTEGER),
        timestamp: makeTimestamp(0),
        user,
      };

      const next = drawReducer(cache, persistedAction);

      if (!is(next.get('userStates'), cache.get('userStates'))) {
        // If the action affects the state, yield it
        yield { action, compressed };
      } else {
        // If the action doesn't affect state just return the amount
        // of actions that were ignored
        yield { compressed };
      }

      cache = next;
    } else {
      yield { action, compressed };
    }
  }
}

/**
 * Collapses any `ICompressionResult`s without actions into the first
 * `ICompressionResult` with an action
 *
 * Removes any `ICompressionResult` without an action and increases
 * `compressed` counter of the preceding `ICompressionResult`
 * that has an action.
 */
function* collapseUndefineds(
  actions: Iterable<ICompressionResult<DrawAction>>,
): Iterable<ICompressionResult<DrawAction>> {
  let tail: ICompressionResult<DrawAction> | null = null;

  for (const { action, compressed } of actions) {
    if (action) {
      if (tail) {
        yield tail;
      }
      tail = { action, compressed };
    } else {
      if (tail) {
        tail = {
          action: tail.action,
          compressed: tail.compressed + compressed,
        };
      } else {
        tail = { action, compressed };
      }
    }
  }

  if (tail) {
    yield tail;
  }
}

const appendTrace = (base: TraceAction, trace: Trace): TraceAction => ({
  ...base,
  trace: [...base.trace, ...trace],
});

/**
 * Merges traces of consecutive trace actions
 *
 * Only collapses `add` actions to the preceding action with a
 * trace. Consecutive `pencil` and `eraser` actions will still be
 * kept separate.
 */
function* mergeTraces(
  actions: Iterable<ICompressionResult<DrawAction>>,
): Iterable<ICompressionResult<DrawAction>> {
  let tail: Required<ICompressionResult<TraceAction>> | null = null;

  for (const { action, compressed } of actions) {
    if (action && isTraceAction(action)) {
      if (isStartTraceAction(action)) {
        // If we encounter an action that starts a new trace,
        // we need to push the old tail out and now the current
        // action becomes the current tail.
        if (tail) {
          yield tail;
        }
        tail = { action, compressed };
      } else {
        // Here action can only be an IAddAction. Either merge
        // trace to the end of the current tail or if there's no
        // current tail, current action becomes the tail
        if (tail) {
          tail = {
            action: appendTrace(tail.action, action.trace),
            compressed: tail.compressed + compressed,
          };
        } else {
          tail = { action, compressed };
        }
      }
    } else {
      // For non-trace actions just push out any tail we had so
      // far and also the current action itself because there's no
      // traces to merge here.
      if (tail) {
        yield tail;
        tail = null;
      }

      yield { action, compressed };
    }
  }

  // Push out any left-over tail
  if (tail) {
    yield tail;
  }
}

/**
 * Removes redundant state changing actions (like `switch`) and
 * merges trace actions
 */
const compress = (
  actions: Iterable<DrawAction>,
  state?: DrawState,
  user?: EntityStringId,
): Iterable<ICompressionResult<DrawAction>> => {
  // Every action starts as 1:1 compression ratio. This is to
  // normalise input and output format
  const base = mapIterable(actions, (action) => ({ action, compressed: 1 }));
  // Make redundant switch actions undefined
  const nonRedundant = clearRedundantStateChanges(base, state, user);
  // Collapse actions that were undefined into first non-undefined action
  const noUndefineds = collapseUndefineds(nonRedundant);
  // Merge consecutive trace actions
  return mergeTraces(noUndefineds);
};

export default compress;
